// Chained-generation wizard: generate → optimize the rounds → send the
// appointment requests. Opened by the Générer button of the preview; the
// whole progress lives inside the modal (stepper on top).
import { Controller } from 'stimulus'
import consumer       from 'channels/consumer'

export default class extends Controller {
  static targets = [
    'overlay', 'closeButton', 'error',
    'setupPhase', 'runPhase', 'donePhase', 'reportPhase', 'reportBody',
    'recapCount', 'recapPeriod', 'recapHours',
    'optimizeToggle', 'optimizeOption', 'pastWarning', 'appointToggle', 'startButton',
    'spreadInput', 'dateChangeToggle', 'techSwapToggle',
    'runLabel', 'runTrack', 'runBar', 'runCount',
    'results', 'listLink', 'planningLink',
  ]

  disconnect() {
    this._unsubscribe()
  }

  open(e) {
    e.preventDefault()
    e.stopImmediatePropagation()

    this.form = e.target.closest('form')
    const boxes = [...this.form.querySelectorAll(
      "input[type='checkbox'][data-target*='preview.checkbox']:checked:not(:disabled)"
    )]

    if (boxes.length === 0) {
      window.alert(I18n.t('maintenance.select_one_workorder'))
      return
    }

    this.running = false
    this.stepResults = {}
    this.period = this.form.querySelector("input[name='period']")?.value || ''
    // Total time of the CHECKED rows only — the preview's global figure also
    // counts the unchecked and already-generated ones.
    const minutes = boxes.reduce((sum, box) => sum + (parseInt(box.dataset.durationMinutes, 10) || 0), 0)
    const hours   = `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`

    this.recapCountTarget.textContent  = boxes.length
    this.recapPeriodTarget.textContent = this.period
    this.recapHoursTarget.textContent  = hours

    this._refreshPastWarning(boxes)

    this._showPhase('setup')
    this.errorTarget.hidden = true
    this.startButtonTarget.disabled = false
    this._eachStep(step => this._setStep(step, 'pending'))
    this._setStep('generate', 'active')
    this.overlayTarget.hidden = false
  }

  close() {
    if (this.running) return

    this._unsubscribe()
    this.overlayTarget.hidden = true
  }

  // ── Phase 2: run ─────────────────────────────────────────────────────────
  start() {
    if (this.running) return

    if (this._spread() === 'custom' &&
        this.hasOptimizeToggleTarget && this.optimizeToggleTarget.checked &&
        this._customTechnicianIds().length === 0) {
      window.alert(I18n.t('workorders.select_technician'))
      return
    }

    this.running = true
    this.startButtonTarget.disabled = true
    this.closeButtonTarget.disabled = true

    this.queue = ['generate']
    if (this.hasOptimizeToggleTarget && this.optimizeToggleTarget.checked) this.queue.push('optimize')
    if (this.hasAppointToggleTarget && this.appointToggleTarget.checked)   this.queue.push('appoint')
    this._eachStep(step => {
      if (!this.queue.includes(step)) this._setStep(step, 'skipped')
    })

    this._showPhase('run')
    this._runGenerate()
  }

  _runGenerate() {
    this._setStep('generate', 'active')
    this._progress(I18n.t('maintenance.generating_progress'), null)

    fetch('/maintenance/workorders/launch', {
      method:      'POST',
      body:        new FormData(this.form),
      credentials: 'same-origin',
      headers:     { 'X-CSRF-Token': this._csrf(), 'Accept': 'application/json' },
    })
      .then(res => res.json().then(json => ({ ok: res.ok, json })))
      .then(({ ok, json }) => {
        if (!ok) return this._abort('generate', json.error)

        this.timestamp = json.timestamp
        this._subscribe(json.heavy_job_id)
      })
      .catch(() => this._abort('generate'))
  }

  _subscribe(heavyJobId) {
    this.subscription = consumer.subscriptions.create(
      { channel: 'HeavyJobStatusChannel', heavy_job_id: heavyJobId },
      { received: data => this._onJobMessage(data) }
    )
  }

  _onJobMessage(data) {
    if (data.status === 'progress') {
      this._progress(data.label, data.total > 0 ? Math.round((data.done / data.total) * 100) : 0,
                     `${data.done} / ${data.total}`)
      return
    }

    this._unsubscribe()

    if (data.status === 'ok') {
      const count  = data.result?.generated_count ?? 0
      const failed = data.result?.failed_count ?? 0
      let text = I18n.t('maintenance.chain.generate_done', { count })
      if (failed > 0) text += ` · ${I18n.t('maintenance.chain.generate_failed', { count: failed })}`
      this.stepResults.generate = text
      this._setStep('generate', failed > 0 ? 'error' : 'done')
      this._next('generate')
    } else {
      if (data.error_detail) console.warn('[generation]', data.error_detail)
      this._abort('generate', data.error_message && I18n.t(data.error_message))
    }
  }

  _next(after) {
    const index = this.queue.indexOf(after)
    const step  = this.queue[index + 1]
    if (!step) return this._finish()

    if (step === 'optimize') this._runOptimize()
    if (step === 'appoint')  this._runAppointments()
  }

  _runOptimize() {
    this._setStep('optimize', 'active')
    this._progress(I18n.t('maintenance.chain.optimize_running'), 'indeterminate')

    this._post('/maintenance/workorders/optimize_batch', {
      spread:         this._spread(),
      technician_ids: this._spread() === 'custom' ? this._customTechnicianIds() : undefined,
      date_change:    this.hasDateChangeToggleTarget ? this.dateChangeToggleTarget.checked : true,
      tech_swap:      this.hasTechSwapToggleTarget ? this.techSwapToggleTarget.checked : true,
    })
      .then(json => {
        if (json.skipped === 'past') {
          this.stepResults.optimize = I18n.t('maintenance.chain.optimize_past_skipped')
          this._setStep('optimize', 'skipped')
          return
        }

        let text = I18n.t('maintenance.chain.optimize_done', { count: json.optimized })
        if (json.unassigned > 0) text += ` · ${I18n.t('maintenance.chain.unassigned', { count: json.unassigned })}`
        this.stepResults.optimize = text
        this._setStep('optimize', 'done')
      })
      .catch(() => this._fail('optimize'))
      .finally(() => this._next('optimize'))
  }

  _runAppointments() {
    this._setStep('appoint', 'active')
    this._progress(I18n.t('maintenance.chain.appoint_running'), 'indeterminate')

    this._post('/maintenance/workorders/send_appointments')
      .then(json => {
        let text = I18n.t('maintenance.chain.appoint_done', { count: json.sent })
        if (json.skipped > 0) text += ` · ${I18n.t('maintenance.chain.appoint_skipped', { count: json.skipped })}`
        this.stepResults.appoint = text
        this._setStep('appoint', 'done')
      })
      .catch(() => this._fail('appoint'))
      .finally(() => this._next('appoint'))
  }

  // ── Phase 3: summary ─────────────────────────────────────────────────────
  _finish() {
    this.running = false
    this.closeButtonTarget.disabled = false

    this.resultsTarget.innerHTML = ''
    this._eachStep(step => {
      if (!this.stepResults[step]) return
      const li     = document.createElement('li')
      const stepEl = this.element.querySelector(`[data-step='${step}']`)
      li.className = stepEl.classList.contains('is-error')   ? 'is-error'
                   : stepEl.classList.contains('is-skipped') ? 'is-warn'
                   : ''
      li.textContent = this.stepResults[step]
      this.resultsTarget.appendChild(li)
    })

    this.listLinkTarget.href = `/workorders?timestamp=${this.timestamp}`
    const firstDate = (this.period.match(/\d{2}\/\d{2}\/\d{4}/) || [])[0]
    this.planningLinkTarget.href = firstDate
      ? `/calendar?selected_date=${firstDate.split('/').reverse().join('-')}`
      : '/calendar'

    this._showPhase('done')
  }

  // Generation failed: nothing downstream makes sense, come back to the setup
  // screen with an explanation instead of a dead end.
  _abort(step, message) {
    this.running = false
    this.closeButtonTarget.disabled = false
    this.startButtonTarget.disabled = false
    this._setStep(step, 'error')
    this.errorTarget.textContent = message || I18n.t('maintenance.chain.error')
    this.errorTarget.hidden = false
    this._showPhase('setup')
  }

  // Optional step failed: mark it, keep chaining.
  _fail(step) {
    this.stepResults[step] = I18n.t('maintenance.chain.error')
    this._setStep(step, 'error')
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  _post(url, extra = {}) {
    return fetch(url, {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': this._csrf(),
        'Accept':       'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ timestamp: this.timestamp, ...extra }),
    }).then(res => { if (!res.ok) throw new Error(res.status); return res.json() })
  }

  showReport() {
    this.donePhaseTarget.hidden = true
    this.reportPhaseTarget.hidden = false
    this.element.querySelector('.genchain__modal').classList.add('genchain__modal--wide')

    if (this.reportLoaded) return
    this.reportBodyTarget.innerHTML =
      `<tr class="genchain__report-loading"><td colspan="5"><span class="genchain__spinner"></span></td></tr>`
    fetch(`/maintenance/workorders/generation_report?timestamp=${this.timestamp}`, {
      credentials: 'same-origin',
      headers:     { 'Accept': 'application/json' },
    })
      .then(res => res.json())
      .then(json => {
        this.reportLoaded = true
        this.reportBodyTarget.innerHTML = ''
        json.rows.forEach(row => {
          const tr = document.createElement('tr')
          const cells = [
            `<td><b>${row.code}</b> ${row.title}</td>`,
            `<td>${row.date}</td>`,
            `<td>${row.technician}</td>`,
          ]
          if (row.optimize)    cells.push(`<td class="is-${row.optimize.state}">${row.optimize.label}</td>`)
          if (row.appointment) cells.push(`<td class="is-${row.appointment.state}">${row.appointment.label}</td>`)
          tr.innerHTML = cells.join('')
          this.reportBodyTarget.appendChild(tr)
        })
      })
      .catch(() => { this.reportBodyTarget.innerHTML = '' })
  }

  hideReport() {
    this.reportPhaseTarget.hidden = true
    this.donePhaseTarget.hidden = false
    this.element.querySelector('.genchain__modal').classList.remove('genchain__modal--wide')
  }

  _progress(label, percent, count = '') {
    this.runLabelTarget.textContent = label || ''
    this.runCountTarget.textContent = count
    if (percent === 'indeterminate') {
      this.runTrackTarget.classList.add('is-indeterminate')
      this.runBarTarget.style.width = '100%'
    } else {
      this.runTrackTarget.classList.remove('is-indeterminate')
      this.runBarTarget.style.width = `${percent || 0}%`
    }
  }

  _setStep(step, state) {
    const li = this.element.querySelector(`[data-step='${step}']`)
    if (!li) return
    li.classList.remove('is-pending', 'is-active', 'is-done', 'is-skipped', 'is-error')
    li.classList.add(`is-${state}`)
  }

  _eachStep(fn) {
    ['generate', 'optimize', 'appoint'].forEach(step => {
      if (this.element.querySelector(`[data-step='${step}']`)) fn(step)
    })
  }

  _showPhase(phase) {
    this.setupPhaseTarget.hidden  = phase !== 'setup'
    this.runPhaseTarget.hidden    = phase !== 'run'
    this.donePhaseTarget.hidden   = phase !== 'done'
    this.reportPhaseTarget.hidden = true
    if (phase === 'setup') this.reportLoaded = false
  }

  _unsubscribe() {
    if (this.subscription) { this.subscription.unsubscribe(); this.subscription = null }
  }

  _csrf() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  _spread() {
    return this.spreadInputTargets.find(input => input.checked)?.value || 'batch'
  }

  // The optimizer rebuilds each round from the technician's working hours on
  // the day itself, so it can only replan from today onwards.
  _refreshPastWarning(boxes) {
    if (!this.hasPastWarningTarget || !this.hasOptimizeToggleTarget) return

    const past = boxes.some(box => this._isPastRow(box))

    this.optimizeToggleTarget.disabled = past
    if (past) {
      this.optimizeToggleTarget.checked = false
      this.stepResults.optimize        = I18n.t('maintenance.chain.optimize_past_skipped')
      this.pastWarningTarget.textContent = I18n.t('maintenance.chain.optimize_past_blocked')
    }
    if (this.hasOptimizeOptionTarget) this.optimizeOptionTarget.classList.toggle('is-locked', past)

    this.pastWarningTarget.hidden = !past
  }

  _isPastRow(box) {
    const input = box.closest('.row')?.querySelector("input[name$='[date_planned]']")
    const match = input?.value.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
    if (!match) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return new Date(`${match[3]}-${match[2]}-${match[1]}T00:00:00`) < today
  }

  // Hidden inputs written by the shared technician_select component.
  _customTechnicianIds() {
    return [...this.element.querySelectorAll("input[name='chain_technicians[]']")]
      .map(input => input.value)
      .filter(Boolean)
  }
}
